import React from 'react';
import { Avatar, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { ButtonsBackground } from 'Components/ButtonsBackground';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Download2 } from 'ImageComponents/Download2';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftBlue } from 'Images';

interface IButtonLinkOrDownloadProps {
  isDownload?: boolean;
  downloadFile?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void | Promise<void>;
  isCallbackNavigation?: boolean;
  callbackNavigation?: () => void;
  label: string;
  url: string;
  isMobileVersion?: boolean;
  isWidthBtn?: boolean;
  icon?: React.ReactNode;
  iconSx?: SxProps<Theme>;
}

export const MAX_CHARS_BTN_LIMIT = 15;

export const ButtonLinkOrDownload = ({
  label,
  url,
  isDownload = false,
  downloadFile,
  isCallbackNavigation,
  callbackNavigation,
  isMobileVersion,
  isWidthBtn,
  icon,
  iconSx,
}: IButtonLinkOrDownloadProps) => {
  const navigate = useNavigate();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Home).userUpdates;
  const isDesktop = useIsDesktop();

  return isMobileVersion ? (
    <Stack
      sx={{
        flexDirection: 'row',
        gap: isDesktop ? '8px' : '6px',
        height: isDesktop ? '56px' : '42px',
        justifyContent: isDownload ? 'space-around' : 'end',
        alignItems: 'center',
        p: isDesktop ? '18px 32px' : '18px 20px',
        position: 'relative',
        overflow: 'hidden',
        mt: isDesktop ? '32px' : '24px',
      }}
    >
      <ButtonsBackground />
      {isDownload && (
        <>
          <Button
            sx={{ gap: isDesktop ? '8px' : '6px', p: 0 }}
            onClick={downloadFile}
            aria-label={dictionary.downloadFile}
          >
            <Download2
              width={isDesktop ? 24 : 20}
              height={isDesktop ? 24 : 20}
            />
            <Typography variant='body5'>{dictionary.download}</Typography>
          </Button>
          <Divider
            sx={{
              borderColor: 'primary.shade_200',
              zIndex: 2,
              height: isDesktop ? undefined : '20px',
              mt: isDesktop ? undefined : '-6px',
            }}
            flexItem
            orientation='vertical'
          />
        </>
      )}

      <Button
        sx={{ gap: isDesktop ? '8px' : '6px', p: 0 }}
        onClick={
          isCallbackNavigation ? callbackNavigation : () => navigate(url)
        }
      >
        <Typography variant='body5'>{label}</Typography>
        {iconSx ? (
          <Box sx={iconSx}>
            {icon ?? (
              <Avatar alt='' src={ArrowLeftBlue} sx={{ width: 16, height: 16 ,marginTop: isDesktop ? 0 : '2px'}} />
            )}
          </Box>
        ) : (
          (icon ?? (
            <Avatar alt='' src={ArrowLeftBlue} sx={{ width: 16, height: 16 ,marginTop: isDesktop ? 0 : '2px'}} />
          ))
        )}
      </Button>
    </Stack>
  ) : (
    <Stack
      sx={{
        flexDirection: 'row',
        cursor: 'pointer',
        backgroundColor: 'primary.shade_100',
        boxShadow: '0px 2px 8px 0px rgba(4, 16, 46, 0.04)',
        border: '1px solid rgba(183, 204, 255, 0.20)',
        borderRadius: '100px',
        p: '10px 16px',
        whiteSpace: isDesktop ? 'nowrap' : 'normal',
        maxWidth: '320px',
        marginRight: isDesktop ? 'auto' : 0,

        width:
          isDownload && isDesktop && !isWidthBtn
            ? '265px'
            : isDownload
              ? '100%'
              : isWidthBtn
                ? 'max-content'
                : '159px',
        justifyContent: isDownload ? 'space-between' : 'center',
        alignItems: 'center',

        '& button': {
          p: 0,
          '&:hover': {
            background: '#A020FF',
            border: 'none',
          },
          '&:focus': {
            backgroundColor: '#081E54',
            border: 'none',
          },
        },
      }}
    >
      {isDownload && (
        <>
          <Button
            sx={{
              width: isDesktop ? 'auto' : '100%',
              height: '100%',
              gap: '6px',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              '&:hover': {
                '& *': {
                  color: '#fff',
                  stroke: isDownload ? '#fff' : undefined,
                },
              },
              '&:focus': {
                '& *': {
                  color: '#fff',
                  stroke: isDownload ? '#fff' : undefined,
                },
              },
            }}
            onClick={downloadFile}
            aria-label={dictionary.downloadFile}
          >
            <Download2
              width={20}
              height={20}
            />
            <Typography variant='body5'>{dictionary.download}</Typography>
          </Button>

          <Divider
            aria-hidden='true'
            orientation='vertical'
            flexItem
            sx={{ borderColor: 'primary.shade_200', m: isDesktop ? '0 4px' : '0 2px'}}
          />
        </>
      )}
      <Button
        onClick={
          isCallbackNavigation ? callbackNavigation : () => navigate(url)
        }
        sx={{
          display: 'flex',
          gap: '4px',
          width: isDesktop ? 'auto' : '100%',
          height: '100%',
          borderTopRightRadius: isDownload ? 0 : undefined,
          borderBottomRightRadius: isDownload ? 0 : undefined,
          '&:hover': {
            '& *': {
              color: '#fff',
              fill: isDownload ? '#fff' : undefined,
            },
          },
          '&:focus': {
            '& *': {
              color: '#fff',
              fill: isDownload ? '#fff' : undefined,
            },
          },
        }}
        aria-label={`${dictionary.navigateTo} ${label}`}
      >
        <Typography variant='body5'>{label}</Typography>
        {iconSx ? (
          <Box sx={iconSx}>
            {icon ?? (
              <Avatar
                alt=''
                src={ArrowLeftBlue}
                sx={{
                  width: isDesktop ? 20 : 16,
                  height: isDesktop ? 20 : 16,
                  marginTop: isDesktop ? 0 : '2px',
                }}
              />
            )}
          </Box>
        ) : (
          (icon ?? (
            <Avatar
              alt=''
              src={ArrowLeftBlue}
              sx={{
                width: isDesktop ? 20 : 16,
                height: isDesktop ? 20 : 16,
                marginTop: isDesktop ? 0 : '2px',
              }}
            />
          ))
        )}
      </Button>
    </Stack>
  );
};
